/* eslint-disable @typescript-eslint/no-explicit-any */
import { ColumnDef } from '@tanstack/react-table';
import { useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { useTranslation } from 'react-i18next';

import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import AdvanceTable from 'components/base/AdvanceTable';
import { ComingResult } from 'data/returning';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { PageBreadcrumbItem } from 'components/common/PageBreadcrumb';
import returnItemService from 'service/returnItemService';
import returnInService from 'service/returnIn';
import returnService from 'service/return';
import Button from 'components/base/Button';
import { showAlert } from 'store/alert/alert.thunk';
import './styles.scss';
import { useAppContext } from 'providers/AppProvider';

const ComingResultReturning = () => {
  const { t, i18n } = useTranslation();
  const {
    config: { theme }
  } = useAppContext();

  const { pathname, state } = useLocation();
  const returningId = pathname.split('/').pop();
  const { returning_pid } = state;
  const dispatch: Dispatch<any> = useDispatch();

  const [canUpdate, setCanUpdate] = useState(false);
  const [loading, setLoading] = useState(false);

  //Fetch Users By ID
  const { refetch: refetchIn } = useQuery(
    ['GET_RETURN_IN_CAN_UPDATE', returningId],
    async () => {
      if (returningId) {
        const res = await returnInService
          .getList({
            offset: 0,
            limit: 10
          })
          .then((res: any) => {
            return res?.returns;
          });

        if (res) {
          const updatedRes = res?.find((el: any) => el?.id === returningId);
          setCanUpdate(updatedRes?.can_update ?? false);
        }
      }
    },
    { enabled: !!returningId }
  );

  const { data, refetch } = useQuery(
    ['GET_RETURN_ITEM_IN', returning_pid],
    () => {
      if (returning_pid) {
        const res = returnItemService
          .getReturnItemInById(returning_pid, {
            offset: 0,
            limit: 10
          })
          .then((res: any) => {
            return res?.return_items;
          });
        return res;
      }
    },
    { enabled: !!returning_pid }
  );

  const comingResultData: ComingResult[] = useMemo(() => {
    const resultData =
      data?.map((items: any) => {
        const packageTotal =
          items?.nomenclature?.measure_unit_kind_pid === 1
            ? (+items.price_in / items?.package_quantity) * +items.quantity
            : +items.price_in * +items.quantity;

        const historyCount =
          items?.price_in && items?.quantity ? packageTotal : 0;

        const packageQuantity = items?.package_quantity || 1;

        const quantity = items?.quantity
          ? Number(items?.quantity.toFixed(3))
          : 0;
        const fullPackages = Math.floor(quantity / packageQuantity);
        const remainingQuantity = quantity % packageQuantity;
        const quantityDisplay = `${fullPackages}/${remainingQuantity}`;

        return {
          guid: items?.id,
          gupid: items?.pid,
          nomenclature_id: items?.nomenclature_id,
          nomenclature_pid: items?.nomenclature_pid,
          recipient_id: items?.recipient_id,
          recipient_pid: items?.recipient_pid,
          product_name: items?.product_short_name,
          product_id: items?.product_id,
          product_pid: items?.product_pid,
          parent_product_id: items?.parent_product_id,
          parent_product_pid: items?.parent_product_pid,
          barcode: items?.barcode,
          currency: items?.currency?.name?.[i18n?.language],
          units: items?.nomenclature?.measure_unit?.name?.[i18n?.language],
          measure_unit_id: items?.nomenclature?.measure_unit?.id,
          measure_unit_pid: items?.nomenclature?.measure_unit?.pid,
          measure_unit_kind_pid: items?.nomenclature?.measure_unit_kind_pid,
          amount:
            items?.nomenclature?.measure_unit_kind_pid === 1
              ? quantityDisplay
              : items?.quantity,
          status: items?.status?.name?.[i18n?.language],
          cost: items?.price_in
            ? new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 4,
                useGrouping: true
              })
                .format(items?.price_in)
                .replace(/,/g, ' ')
            : 0,
          remainder: items?.total_quantity ?? 0,
          /* prettier-ignore */
          history: historyCount
            ? new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 4,
                useGrouping: true
              })
                .format(historyCount)
                .replace(/,/g, ' ')
            : 0
        };
      }) ?? [];

    return resultData;
  }, [data, t, i18n?.language]);

  const comingResultTableColumns: ColumnDef<ComingResult>[] = [
    {
      accessorKey: 'product_name',
      header: t('product_names'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'barcode',
      header: t('barcode'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'currency',
      header: t('currency'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'units',
      header: t('measure_unit'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'cost',
      header: t('wholesale_price'),
      meta: {
        cellProps: { className: 'text-900', style: { textAlign: 'right' } }
      }
    },

    {
      accessorKey: 'amount',
      header: t('short_quantity_return'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'history',
      header: t('final_purchase_price'),
      meta: {
        cellProps: { className: 'text-900', style: { textAlign: 'right' } }
      }
    },
    {
      accessorKey: 'status',
      header: t('status'),
      cell: rowData => {
        const { status } = rowData?.row?.original || {};
        /* prettier-ignore */
        const defaultColors: any = {
          Завершен: { bg: '#BBFBD0', text: '#119C2B' },
          Отправлен: { bg: '#fef3c7', text: '#92400e' },
          Bajarildi: { bg: '#BBFBD0', text: '#119C2B' },
          "Jo'natilgan": { bg: '#fef3c7', text: '#92400e' }
        };
        /* prettier-ignore */
        const backgroundColor =
         defaultColors[status]?.bg || '#ffffff'; // Default to white if not specified
        const textColor = defaultColors[status]?.text || '#000000';

        return (
          <div
            style={{
              background: backgroundColor,
              padding: '8px 10px',
              textAlign: 'center',
              borderRadius: '8px',
              color: textColor,
              fontWeight: '600'
            }}
          >
            {status}
          </div>
        );
      },
      meta: {
        cellProps: { className: 'text-900' }
      }
    }
  ];

  const buyyersBreadcrumbItems: PageBreadcrumbItem[] = [
    {
      label: 'returns',
      url: '/returning/outgoing'
    },
    {
      label: 'incoming',
      url: '/returning/coming'
    },
    {
      label: 'application_number',
      active: true
    }
  ];

  const table = useAdvanceTable({
    data: comingResultData,
    columns: comingResultTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  // const sum = comingResultData.reduce((acc: any, item: any) => {
  //   const allCost =
  //     item?.history === undefined ? 0 : +item?.history?.split(' ')?.join('');
  //   return acc + allCost;
  // }, 0);

  console.log('comingResultData', comingResultData);

  const sum = comingResultData.reduce((acc: number, item: any) => {
    if (typeof item?.history !== 'string') {
      console.warn(
        `Invalid history value for item:`,
        item,
        `type: ${typeof item.history}`
      );
      return acc + 0;
    }
    const allCost = +item.history.split(' ').join('');
    return acc + allCost;
  }, 0);

  const handleSave = () => {
    setLoading(true);
    returnService.getReturnById(returning_pid).then((res: any) => {
      if (res) {
        returnInService
          ?.updateReturnIn(returning_pid, {
            ...res,
            status_id: '00525476-8e64-40bc-9dd2-083583c95b30',
            status_pid: 9
          })
          .then((inresponse: any) => {
            if (inresponse) {
              dispatch(
                showAlert({
                  title: t('answered_successfully'),
                  type: 'success'
                })
              );
              setLoading(false);
              refetch();
              refetchIn();
            }
          });
      }
    });
  };

  const handleFinished = () => {
    setLoading(true);
    if (returning_pid) {
      returnService.getReturnById(returning_pid).then((res: any) => {
        if (res) {
          returnInService
            ?.updateReturnIn(returning_pid, {
              ...res,
              status_id: '4d22cd85-070e-4779-afcb-8f0be603b60d',
              status_pid: 4
            })
            .then((inresponse: any) => {
              if (inresponse) {
                dispatch(
                  showAlert({
                    title: t('rejected_successfully'),
                    type: 'success'
                  })
                );
                setLoading(false);
                refetch();
                refetchIn();
              }
            });
        }
      });
    }
  };

  return (
    <div>
      <PageBreadcrumb items={buyyersBreadcrumbItems} />

      <div>
        <h2 className="mb-5">{t('application_number')}</h2>

        <AdvanceTableProvider {...table}>
          <div className="mt-4 mx-n4 px-4 mx-lg-n6 px-lg-6 bg-white border-top border-bottom border-300 position-relative top-1">
            <AdvanceTable tableProps={{ className: 'phoenix-table fs-9' }} />
            <AdvanceTableFooter pagination />
          </div>
        </AdvanceTableProvider>
        <div
          className={
            theme === 'dark' ? 'dark-footer-ret-coming' : 'footer-ret-coming'
          }
        >
          <div className="box">
            <p className="mb-0 text-bold">{t('total_amount')}</p>
            <span>
              {sum
                ? new Intl.NumberFormat('en-US', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 4,
                    useGrouping: true
                  })
                    .format(sum)
                    .replace(/,/g, ' ')
                : 0}
            </span>
          </div>

          <div className="main-btn-group">
            <Button
              onClick={() => handleSave()}
              disabled={!canUpdate || loading}
              variant="primary"
              loading={loading}
              loadingPosition="start"
            >
              {t('answer')}
            </Button>
            <Button
              onClick={() => handleFinished()}
              disabled={!canUpdate || loading}
              variant="danger"
              loading={loading}
              loadingPosition="start"
            >
              {t('reject')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComingResultReturning;
